import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import type { CrsRule, Site, SiteWafDirective } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Icon } from "~/assets/icons/Icon";
import Button from "~/components/mantine/button/Button";
import NotificationBlock from "~/components/notificationBlock/NotificationBlock";
import { useDraggableList } from "~/hooks/useDraggableList";
import humanizeString from "~/pages/GoogleDashboardPage/utils/humanizeString";
import { routes } from "~/router";

import FormLabel from "../login/formLabel/FormLabel";
import Switch from "../switch/Switch";
import { CollapsibleSection } from "../ui/collapsibleSection";
import Spinner from "../ui/spinner/Spinner";
import { SiteWithAuth } from "./General";
import { SiteSettingsFormValues } from "./SiteSettings";
import styles from "./SiteSettings.module.scss";
import { WafDirectiveModal } from "./waf/WafDirectiveModal";
import { CRS_GROUP_INFO } from "./waf/crsGroups";

const INTEGRATION_TOOLTIP_ID = "waf-integration-disabled";
const INTEGRATION_TOOLTIP_TEXT =
  "Only available with Full or Easy integration mode.";

const WAF_LEVELS = [
  {
    value: "lite" as const,
    label: "Lite",
    description: "Basic filtering. Minimal false positives.",
  },
  {
    value: "normal" as const,
    label: "Normal",
    description: "Balanced protection. Recommended for most sites.",
  },
  {
    value: "strict" as const,
    label: "Strict",
    description: "Maximum protection. May block some legitimate traffic.",
  },
];

const WAF_EXCLUSION_OPTIONS: {
  value: Site["waf_exclusions"][number];
  label: string;
  description: string;
}[] = [
  {
    value: "wordpress",
    label: "WordPress",
    description:
      "Excludes false positives triggered by WordPress core, admin, and REST API requests.",
  },
  {
    value: "woocommerce",
    label: "WooCommerce",
    description:
      "Excludes false positives triggered by WooCommerce cart, checkout, and customer account endpoints.",
  },
  {
    value: "drupal",
    label: "Drupal",
    description:
      "Excludes false positives triggered by Drupal core and admin paths.",
  },
  {
    value: "nextcloud",
    label: "Nextcloud",
    description:
      "Excludes false positives triggered by Nextcloud WebDAV and sync endpoints.",
  },
  {
    value: "cpanel",
    label: "cPanel",
    description:
      "Excludes false positives triggered by cPanel and WHM management interfaces.",
  },
  {
    value: "dokuwiki",
    label: "DokuWiki",
    description:
      "Excludes false positives triggered by DokuWiki editing and admin pages.",
  },
  {
    value: "phpbb",
    label: "phpBB",
    description:
      "Excludes false positives triggered by phpBB forum posting and admin.",
  },
  {
    value: "phpmyadmin",
    label: "phpMyAdmin",
    description:
      "Excludes false positives triggered by phpMyAdmin SQL editor, import/export, and query operations.",
  },
  {
    value: "xenforo",
    label: "XenForo",
    description:
      "Excludes false positives triggered by XenForo forum posting and admin.",
  },
];

type WafSettingsProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
};

const LEVEL_PARANOIA: Record<"normal" | "strict", number> = {
  normal: 1,
  strict: 2,
};

function summarizeTargets(
  d: SiteWafDirective,
  availableGroupIds: number[],
): string {
  const parts: string[] = [];

  if (d.groups.length > 0) {
    const names = d.groups
      .slice(0, 3)
      .map((g) => CRS_GROUP_INFO[g]?.name ?? `Group ${g}`)
      .join(", ");

    parts.push(
      `${names}${d.groups.length > 3 ? ` +${d.groups.length - 3} more` : ""}`,
    );
  }

  if (d.rules.length > 0) {
    parts.push(`${d.rules.length} rule${d.rules.length > 1 ? "s" : ""}`);
  }

  if (d.groups.length === 0 && d.rules.length === 0) {
    return `All rules (${availableGroupIds.length} groups)`;
  }

  return parts.join(" · ");
}

function summarizeConditions(d: SiteWafDirective): string {
  const c = d.conditions;
  const parts: string[] = [];

  if (c.path) {
    const k = Object.keys(c.path)[0];
    const vals = (c.path as Record<string, string[]>)[k] ?? [];
    parts.push(
      `path ${k}: ${vals[0]}${vals.length > 1 ? ` +${vals.length - 1}` : ""}`,
    );
  }

  if (c.method?.in?.length) parts.push(`method: ${c.method.in.join(", ")}`);
  if (c.scheme?.in?.length) parts.push(`scheme: ${c.scheme.in.join(", ")}`);
  if (c.host) parts.push(`host`);
  if (c.ip?.cidr?.length) parts.push(`ip (${c.ip.cidr.length} CIDR)`);
  if (c.content_type) parts.push(`content-type`);
  if (c.query) parts.push(`query (${c.query.match.length})`);
  if (c.headers) parts.push(`headers (${c.headers.match.length})`);
  if (c.cookies) parts.push(`cookies (${c.cookies.match.length})`);

  return parts.length > 0 ? parts.join(" · ") : "No conditions";
}

function ActionBadge({ action }: { action: "log" | "allow" | "deny" }) {
  const cls =
    action === "log"
      ? styles.wafBadgeLog
      : action === "allow"
        ? styles.wafBadgeAllow
        : styles.wafBadgeBlock;

  return <span className={cls}>{action}</span>;
}

const WafSettings = ({ siteData, isLoading }: WafSettingsProps) => {
  const qc = useQueryClient();
  const { watch, setValue } = useFormContext<SiteSettingsFormValues>();

  const canUpdateSite = siteData?.authorizations?.member
    ?.flat()
    .includes("update");

  // The WAF lives behind the AI Visibility Engine proxy — without it,
  // there's no traffic to filter. Render the section read-only when the
  // user hasn't enabled prerendering yet, and prompt them to turn it on.
  const prerenderingEnabled = !!siteData?.site?.prerendering;
  // The WAF runs inside Alli AI's DNS/cert layer, so it's only meaningful
  // for full/partial integrations. Edge and plugin route traffic via the
  // customer's own infra, where we can't filter it.
  const integrationType = siteData?.site?.delegated_domain?.integration_type;
  const firewallSupportedByIntegration =
    integrationType === "full" || integrationType === "partial";
  const formDisabled =
    !canUpdateSite || !prerenderingEnabled || !firewallSupportedByIntegration;
  // Only show the "wrong mode" tooltip when an explicit unsupported
  // integration has been chosen — pre-wizard nulls aren't a "wrong mode".
  const showIntegrationTooltip =
    integrationType === "edge" || integrationType === "plugin";
  const tooltipAttrs = showIntegrationTooltip
    ? {
        "data-tooltip-id": INTEGRATION_TOOLTIP_ID,
        "data-tooltip-content": INTEGRATION_TOOLTIP_TEXT,
      }
    : {};

  const wafEnabled = watch("waf_enabled");
  const wafLevel = watch("waf_level");
  const wafExclusions = watch("waf_exclusions") ?? [];
  const siteId = siteData?.site?.id;

  const showRulesPanel = wafEnabled && wafLevel && wafLevel !== "lite";
  const showDirectives = wafEnabled && wafLevel;
  const paranoia =
    wafLevel && wafLevel !== "lite"
      ? LEVEL_PARANOIA[wafLevel as "normal" | "strict"]
      : null;

  // ── Data fetching ──────────────────────────────────────────

  const { data: crsData, isLoading: crsLoading } = useQuery({
    queryKey: queryKeys.crsRules(),
    queryFn: () => apiClient.crsRules.crsRulesList(),
    enabled: !!showRulesPanel,
    staleTime: 10 * 60 * 1000,
  });

  const directivesQueryKey = queryKeys.wafDirectives(siteId ?? 0);

  const { data: directivesData, isLoading: directivesLoading } = useQuery({
    queryKey: directivesQueryKey,
    queryFn: () =>
      apiClient.sites.wafDirectivesDetail(siteId!, { "page[size]": 200 }),
    enabled: !!siteId && !!showDirectives,
  });

  // ── Filtered / grouped rules ───────────────────────────────

  const filteredRules = useMemo<CrsRule[]>(() => {
    if (!crsData?.data?.crs_rules || paranoia === null) return [];

    return crsData.data.crs_rules.filter((r) => r.paranoia <= paranoia);
  }, [crsData, paranoia]);

  const rulesByGroup = useMemo(() => {
    const map = new Map<number, CrsRule[]>();

    filteredRules.forEach((r) => {
      if (!map.has(r.group)) map.set(r.group, []);
      map.get(r.group)!.push(r);
    });

    return map;
  }, [filteredRules]);

  const availableGroupIds = useMemo(
    () => Array.from(rulesByGroup.keys()).sort((a, b) => a - b),
    [rulesByGroup],
  );

  const directives = directivesData?.data?.site_waf_directives ?? [];

  // ── Accordion state ────────────────────────────────────────

  const [openGroupIds, setOpenGroupIds] = useState<Set<number>>(new Set());

  const toggleGroup = (gid: number) =>
    setOpenGroupIds((prev) => {
      const next = new Set(prev);

      next.has(gid) ? next.delete(gid) : next.add(gid);

      return next;
    });

  // ── Directive modal ────────────────────────────────────────

  const [modalDirective, setModalDirective] = useState<
    SiteWafDirective | "new" | null
  >(null);

  // ── Delete directive ───────────────────────────────────────

  const { mutate: deleteDirective, isPending: isDeleting } = useMutation({
    mutationFn: (id: number) =>
      apiClient.sites.wafDirectivesDelete(siteId!, id),
    onSuccess: (_, deletedId) => {
      enqueueSnackbar("Directive deleted.", { variant: "success" });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      qc.setQueryData(directivesQueryKey, (old: any) => {
        if (!old?.data?.site_waf_directives) return old;

        return {
          ...old,
          data: {
            ...old.data,
            site_waf_directives: old.data.site_waf_directives.filter(
              (d: SiteWafDirective) => d.id !== deletedId,
            ),
          },
        };
      });
      void qc.invalidateQueries({ queryKey: directivesQueryKey });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete directive.", {
        variant: "error",
        persist: true,
      });
    },
  });

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // ── Drag-to-reorder ────────────────────────────────────────

  const { mutate: reorderDirective } = useMutation({
    mutationFn: ({ id, priority }: { id: number; priority: number }) =>
      apiClient.sites.wafDirectivesPartialUpdate(siteId!, id, { priority }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: directivesQueryKey });
    },
    onError: () => {
      enqueueSnackbar("Failed to reorder directive.", { variant: "error" });
      void qc.invalidateQueries({ queryKey: directivesQueryKey });
    },
  });

  const {
    isDragging,
    isDragOver,
    handlers: dndHandlers,
  } = useDraggableList(directives, (reordered, moved, newIndex) => {
    // Optimistic update
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qc.setQueryData(directivesQueryKey, (old: any) => {
      if (!old?.data?.site_waf_directives) return old;

      return { ...old, data: { ...old.data, site_waf_directives: reordered } };
    });

    reorderDirective({ id: moved.id, priority: newIndex + 1 });
  });

  // ── Render ─────────────────────────────────────────────────

  if (isLoading) return <Spinner />;

  return (
    <div className={styles.settingsSection}>
      <p className={styles.settingsSectionText}>
        Filters malicious traffic before it reaches your site.
      </p>

      <div className={styles.settingsSectionCrawlerForm}>
        {!prerenderingEnabled && siteId && (
          <NotificationBlock type="info">
            The Web Application Firewall runs at the edge of the AI Visibility
            Engine proxy. Enable{" "}
            <Link
              to={routes.site.visibilityEngineSettings({ id: String(siteId) })}
              style={{ fontWeight: 600 }}
            >
              AI Visibility Engine Optimization
            </Link>{" "}
            first to start protecting your site.
          </NotificationBlock>
        )}

        {prerenderingEnabled && !firewallSupportedByIntegration && (
          <NotificationBlock type="info">
            The Web Application Firewall is only available with{" "}
            <strong>Full</strong> or <strong>Easy</strong> integration. Your
            domain currently uses a different integration mode, so traffic
            doesn&apos;t pass through our filtering layer.
          </NotificationBlock>
        )}

        <div className={styles.settingsSectionGroup}>
          <div className={styles.settingsSwitchGroup}>
            <FormLabel label="Enable WAF" />
            <span {...tooltipAttrs}>
              <Switch
                checked={!!wafEnabled}
                disabled={formDisabled}
                onChange={(e) => {
                  setValue("waf_enabled", e.currentTarget.checked, {
                    shouldDirty: true,
                  });
                }}
              />
            </span>
          </div>

          {wafEnabled && (
            <div className={styles.wafSection}>
              <div className={styles.wafPanelTitle}>Protection Level</div>
              <div className={styles.wafLevelGroup}>
                {WAF_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    disabled={formDisabled}
                    className={
                      wafLevel === level.value
                        ? styles.wafLevelButtonActive
                        : styles.wafLevelButton
                    }
                    onClick={() => {
                      setValue("waf_level", level.value, { shouldDirty: true });
                    }}
                  >
                    <span className={styles.wafLevelButtonLabel}>
                      {level.label}
                    </span>
                    <span className={styles.wafLevelButtonDesc}>
                      {level.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {wafEnabled && wafLevel === "lite" && (
            <div className={styles.wafLiteInfo}>
              <div className={styles.wafLiteInfoIcon}>⚠️</div>
              <div className={styles.wafLiteInfoBody}>
                <div className={styles.wafLiteInfoTitle}>
                  Minimal Protection
                </div>
                <div className={styles.wafLiteInfoText}>
                  Lite mode runs only basic request validation — no OWASP CRS
                  rules, no threat scoring, no paranoia levels. It will not
                  block common attacks such as SQL injection or XSS. Use Normal
                  or Strict for real protection. You can still add custom
                  directives below to enforce your own rules.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showRulesPanel && (
        <>
          {crsLoading ? (
            <Spinner />
          ) : (
            <>
              {/* ── CRS Rules reference (collapsed) ── */}
              <CollapsibleSection
                title={`OWASP CRS Rules — ${humanizeString(wafLevel!)}`}
                subtitle={`${filteredRules.length} rules across ${availableGroupIds.length} groups. Use directives below to customise how they are applied.`}
                className={styles.wafSection}
              >
                <div className={styles.wafGroupsAccordion}>
                  {availableGroupIds.map((gid) => {
                    const rules = rulesByGroup.get(gid) ?? [];
                    const info = CRS_GROUP_INFO[gid];
                    const isOpen = openGroupIds.has(gid);

                    return (
                      <div key={gid} className={styles.wafGroupRow}>
                        <button
                          type="button"
                          className={styles.wafGroupHeader}
                          aria-expanded={isOpen}
                          onClick={() => toggleGroup(gid)}
                        >
                          <span
                            className={
                              isOpen
                                ? `${styles.wafGroupChevron} ${styles.wafGroupChevronOpen}`
                                : styles.wafGroupChevron
                            }
                          >
                            ▼
                          </span>
                          <div className={styles.wafGroupBody}>
                            <div className={styles.wafGroupTitleRow}>
                              <span className={styles.wafGroupNum}>{gid}</span>
                              <span className={styles.wafGroupName}>
                                {info?.name ?? `Group ${gid}`}
                              </span>
                            </div>
                            {info?.description && (
                              <span className={styles.wafGroupDesc}>
                                {info.description}
                              </span>
                            )}
                          </div>
                          <span className={styles.wafGroupCount}>
                            {rules.length}
                          </span>
                        </button>

                        {isOpen && (
                          <div className={styles.wafRulesList}>
                            {rules.map((rule) => (
                              <div key={rule.id} className={styles.wafRuleItem}>
                                <span className={styles.wafRuleItemId}>
                                  {rule.id}
                                </span>
                                {rule.message && (
                                  <span className={styles.wafRuleItemMessage}>
                                    {rule.message}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>

              {/* ── Platform Exclusions ── */}
              <CollapsibleSection
                title="Platform Exclusions"
                subtitle="If your site runs on a known platform, enable its exclusion profile to avoid false positives."
                defaultOpen
                className={styles.wafSection}
              >
                <div className={styles.wafExclusionsGrid}>
                  {WAF_EXCLUSION_OPTIONS.map((opt) => {
                    const isSelected = wafExclusions.includes(opt.value);

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={formDisabled}
                        className={
                          isSelected
                            ? styles.wafExclusionChipActive
                            : styles.wafExclusionChip
                        }
                        title={opt.description}
                        onClick={() => {
                          const next = isSelected
                            ? wafExclusions.filter((v) => v !== opt.value)
                            : [...wafExclusions, opt.value];

                          setValue("waf_exclusions", next, {
                            shouldDirty: true,
                          });
                        }}
                      >
                        <span className={styles.wafExclusionChipIcon}>
                          {isSelected ? (
                            <Icon
                              iconName="CheckmarkIcon"
                              width="0.875rem"
                              height="0.875rem"
                            />
                          ) : (
                            <span className={styles.wafExclusionChipCircle} />
                          )}
                        </span>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </CollapsibleSection>
            </>
          )}
        </>
      )}

      {showDirectives && (
        <CollapsibleSection
          title="Directives"
          subtitle="Override rule behaviour for matching requests, evaluated by priority."
          defaultOpen
          className={styles.wafSection}
          rightAction={
            <Button
              type="button"
              size="sm"
              disabled={formDisabled}
              onClick={() => setModalDirective("new")}
            >
              + Add Directive
            </Button>
          }
        >
          {directivesLoading ? (
            <Spinner />
          ) : directives.length === 0 ? (
            <div className={styles.wafEmptyDirectives}>
              No directives yet. Add one to customise how rules are applied.
            </div>
          ) : (
            <div className={styles.wafDirectivesList}>
              {directives.map((d, idx) => (
                <div
                  key={d.id}
                  className={[
                    styles.wafDirectiveCard,
                    isDragging(idx) ? styles.wafDirectiveCardDragging : "",
                    isDragOver(idx) ? styles.wafDirectiveCardDragOver : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  draggable={!formDisabled}
                  {...(!formDisabled ? dndHandlers(idx) : {})}
                >
                  {!formDisabled && (
                    <span
                      className={styles.wafDragHandle}
                      title="Drag to reorder"
                    >
                      ⠿
                    </span>
                  )}
                  <span className={styles.wafDirectivePriority}>
                    #{d.priority}
                  </span>
                  <ActionBadge action={d.action} />
                  <div className={styles.wafDirectiveMeta}>
                    <span className={styles.wafDirectiveTargets}>
                      {summarizeTargets(d, availableGroupIds)}
                    </span>
                    <span className={styles.wafDirectiveConditions}>
                      {summarizeConditions(d)}
                    </span>
                  </div>
                  <div className={styles.wafDirectiveActions}>
                    {confirmDeleteId === d.id ? (
                      <>
                        <span className={styles.wafDirectiveConfirmText}>
                          Delete?
                        </span>
                        <button
                          type="button"
                          className={styles.wafDirectiveBtnDanger}
                          disabled={isDeleting}
                          onClick={() => {
                            deleteDirective(d.id);
                            setConfirmDeleteId(null);
                          }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={styles.wafDirectiveBtn}
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          No
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.wafDirectiveBtn}
                          disabled={formDisabled}
                          onClick={() => setModalDirective(d)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={styles.wafDirectiveBtnDanger}
                          disabled={formDisabled}
                          onClick={() => setConfirmDeleteId(d.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      )}

      {modalDirective !== null && siteId !== undefined && (
        <WafDirectiveModal
          siteId={siteId}
          directive={modalDirective === "new" ? null : modalDirective}
          availableRules={filteredRules}
          availableGroupIds={availableGroupIds}
          onClose={() => setModalDirective(null)}
          queryKey={directivesQueryKey}
        />
      )}
      {showIntegrationTooltip && <Tooltip id={INTEGRATION_TOOLTIP_ID} />}
    </div>
  );
};

export default WafSettings;
